import { useQueryClient } from '@tanstack/react-query';
import { Camera, ExternalLink, Loader2, ScanLine, ShieldCheck, X } from 'lucide-react';
import QRCode from 'qrcode';
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  AadhaarCardMock,
  AadhaarOcrScreen,
  AadhaarOvseScreen,
  AadhaarSecureQrScreen,
  Field,
  PreviewHeader,
} from '@/features/workflows/components/simple/preview-screens';
import { toast } from '@/lib/notify';

import { sessionKeys } from '../../api/sessionKeys';
import { publicSessionKeys } from '../../api/usePublicSession';
import { useVerifyStep } from '../../api/useVerifyStep';
import { usePublicRun } from '../../publicRun';
import type {
  SessionDetailDto,
  SessionStepApiBindingDto,
  SessionStepDto,
} from '../../schemas/sessionSchemas';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';
import {
  CHECK_FAILED_TOAST,
  RETRY_EXHAUSTED_NOTE,
  tryAnotherMethodNote,
} from '../stepFallbackNotes';
import { AadhaarDevSimulator } from './AadhaarDevSimulator';
import { AadhaarQrScanner } from './AadhaarQrScanner';
import {
  AADHAAR_METHOD_LABEL,
  type AadhaarScreenKind,
  aadhaarScreenKind,
  extractAadhaarReason,
  findRequestUrl,
  isAsyncKind,
} from './aadhaar.utils';
import { canScanQrLive, readAadhaarQrPayload } from './aadhaarQrPayload';

// Fallback binding for legacy sessions whose snapshot carries no bindings.
const DEFAULT_BINDING: SessionStepApiBindingDto = {
  catalogueEntryId: '',
  path: '/api/v1/digilocker/init',
  operationId: 'Post_Aadhaar_Digilocker_Pinless',
  summary: 'Aadhaar Verification',
  priority: 1,
};

/**
 * Runs one Aadhaar Verification step in a live session.
 *
 *  • DigiLocker (PIN / PIN-less) and OVSE are ASYNC: the /init call returns a
 *    redirect / QR URL. We open it in a controlled child window (inside our
 *    app via a modal shell — never a full-page redirect) and poll the session.
 *    SignCare calls our webhook when the applicant finishes, which flips the
 *    step to Passed; the poll picks that up and the parent advances the flow.
 *  • Aadhaar OCR is SYNCHRONOUS: upload / scan the card → scored on the response.
 *
 * On a technical init failure we fall back to the next enabled binding
 * (visible priority fallback), mirroring the PAN runner.
 */
export function AadhaarStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const qc = useQueryClient();
  const { mutate, isPending } = useVerifyStep(session.id);
  // Non-null in the anonymous public-link run — the poll must refresh the PUBLIC
  // session cache (keyed by token), not the tenant one.
  const publicRun = usePublicRun();

  // Where DigiLocker returns the applicant's browser after they finish. Anonymous
  // run → their per-session page (/w/{token}), which always exists because the
  // session is created when the mobile number is verified; internal fill → the
  // admin fill page. Sent to the gateway as redirect_url for a same-tab round-trip.
  const origin = window.location.origin;
  const digilockerReturnUrl = publicRun
    ? `${origin}/w/${publicRun.token}`
    : `${origin}/sessions/${session.id}/fill`;

  const bindings = useMemo(() => {
    const b = step.apiBindings.slice().sort((x, y) => x.priority - y.priority);
    return b.length > 0 ? b : [DEFAULT_BINDING];
  }, [step.apiBindings]);

  const [activeIdx, setActiveIdx] = useState(0);
  const active = bindings[activeIdx] ?? bindings[0]!;
  const kind = aadhaarScreenKind(active);

  const [mobile, setMobile] = useState('');
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  // The Secure QR's decimal payload, when this device could read it out of the captured
  // image itself. Null means "send the image and let the server decode it" — the original
  // behaviour, and still what happens on any browser without BarcodeDetector.
  const [qrPayload, setQrPayload] = useState<string | null>(null);
  // Live scanner open. Starts ON for Secure QR wherever a camera and a decoder both exist,
  // because scanning is the better route: it retries every frame instead of betting the
  // whole attempt on one still photo, and it hands the gateway a payload so no image has to
  // be decoded server-side. "Take a photo instead" closes it, and any device that cannot
  // scan never sees it.
  const [scanning, setScanning] = useState(kind === 'secure-qr' && canScanQrLive());
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);

  // Always begin on the input screen (Start button). We call SignCare ONLY when
  // the applicant clicks Start — reopening a pending step must not auto-fire the
  // API or jump straight into the waiting/QR state. Each Start begins a fresh
  // verification attempt (a new init / QR).
  const [phase, setPhase] = useState<'input' | 'awaiting'>('input');
  // OVSE: the maadhaar intent URL + the QR we draw from it CLIENT-SIDE. The QR
  // data URL lives only in React state — it is never sent to or stored by the
  // backend (base64 QRs must not bloat the DB).
  const [ovseUrl, setOvseUrl] = useState<string | null>(null);
  const [ovseQr, setOvseQr] = useState<string | null>(null);
  // Set when a submit is blocked because the session's validity window elapsed.
  const [expired, setExpired] = useState(false);
  // DigiLocker: the request_url the applicant is redirected to (same tab).
  const requestUrlRef = useRef<string | null>(null);

  // Render the OVSE QR from the short intent URL, in-memory only.
  useEffect(() => {
    if (!ovseUrl) return;
    let cancelled = false;
    QRCode.toDataURL(ovseUrl, { width: 240, margin: 1 })
      .then((dataUrl) => !cancelled && setOvseQr(dataUrl))
      .catch(() => !cancelled && setOvseQr(null));
    return () => {
      cancelled = true;
    };
  }, [ovseUrl]);

  const cameraRef = useRef<HTMLInputElement>(null);

  // Restore the awaiting state when arriving on a step that already kicked off an
  // async flow (DigiLocker / OVSE init returned a redirect/QR and the step is
  // Pending). This happens when the applicant returns from the provider redirect,
  // and on any mid-flow resume — without it the applicant would see the input form
  // again and re-initialise (a redundant provider call). Runs once.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || step.status !== 'Pending') return;
    const url = findRequestUrl(step.resultJson);
    if (!url) return;
    restoredRef.current = true;
    if (kind === 'ovse') {
      setOvseUrl(url);
    } else if (isAsyncKind(kind)) {
      requestUrlRef.current = url;
    }
    setPhase('awaiting');
  }, [kind, step.status, step.resultJson]);

  // While awaiting the webhook, poll the session so the completed step flips to
  // Passed and the parent advances (closing this runner + its popup).
  useEffect(() => {
    if (phase !== 'awaiting') return;
    const id = window.setInterval(() => {
      // Public run → refresh by token (the tenant cache doesn't exist here);
      // internal run → refresh by session id.
      qc.invalidateQueries({
        queryKey: publicRun
          ? publicSessionKeys.detail(publicRun.token)
          : sessionKeys.detail(session.id),
      });
    }, 3000);
    return () => window.clearInterval(id);
  }, [phase, qc, session.id, publicRun]);

  // Session timed out (Settings → Session Expiry) → neutral "expired" screen.
  // `expired` is set when a Verify is rejected mid-flow; `session.isExpired` is
  // the backend's on-load flag so a resumed-but-expired session shows this
  // immediately instead of the form.
  if (expired || session.isExpired) {
    return <SessionExpiredScreen />;
  }

  // Business reject (step Failed) → terminal rejection screen. A reject ends the
  // session (it is now Rejected), so there is no retry.
  if (step.status === 'Failed') {
    return <FailureScreen reason={extractAadhaarReason(step.resultJson)} />;
  }

  // Same-tab redirect to DigiLocker. The gateway returns the browser to
  // digilockerReturnUrl when done, where the run continues to the next step.
  function goToDigilocker(url: string) {
    requestUrlRef.current = url;
    window.location.assign(url);
  }

  function validate(k: AadhaarScreenKind): boolean {
    if (k === 'ocr' || k === 'secure-qr') return imageDataUrl !== null;
    if (k === 'ovse') return true; // OVSE starts from a QR — no applicant input.
    // DigiLocker WITH PIN needs nothing from us: the gateway sends `using_aadhaar: true`
    // and the applicant types their Aadhaar number, OTP and security PIN inside DigiLocker
    // itself. Demanding a mobile number here blocked Continue on a field the flow never
    // used. It stays on the screen as optional, because a number we do have is passed
    // through and saves the applicant one entry.
    //
    // PIN-LESS is different and still requires it: that route is a MeriPehchaan login,
    // where the mobile is what identifies the account being logged into.
    if (k === 'digilocker-pin') return true;
    return mobile.replace(/\D/g, '').length >= 10;
  }

  function buildInput(k: AadhaarScreenKind): Record<string, unknown> {
    // The AadhaarStepVerifier (backend) shapes the full provider payload and
    // injects the webhook URL — the runner only supplies the applicant-provided
    // bits. Image flows send the raw base64 (no data: prefix).
    const base64 = (imageDataUrl ?? '').replace(/^data:[^;]+;base64,/, '');
    if (k === 'ocr') return { fileBase64: base64 };
    // Prefer the decimal payload when the device managed to read the QR itself: the
    // gateway then parses the payload directly instead of hunting for a QR in a photo.
    // The backend already accepts either — see AadhaarStepVerifier — and sends
    // include_photo either way, so the face reference is captured on both routes.
    if (k === 'secure-qr') return qrPayload ? { payload: qrPayload } : { data: base64 };
    if (k === 'ovse') return {};
    // DigiLocker: send the browser return URL so the gateway sends the applicant
    // back to the run (same-tab round-trip) once they finish.
    return { mobileNumber: mobile.trim() || undefined, redirectUrl: digilockerReturnUrl };
  }

  function advanceOrError(updated: SessionStepDto | undefined) {
    // Business rejection (Failed) is a FINAL verdict — do NOT fall back to the
    // next method. The parent re-renders the failure screen from the updated
    // step. Fallback happens only on a technical failure (step stays Pending).
    if (updated?.status === 'Failed') return;
    const next = bindings[activeIdx + 1];
    if (next) {
      // Named by what the applicant will DO next (scan a QR, open DigiLocker, take a
      // photo) — never `summary`, which is our internal route name. Each Aadhaar method
      // changes the screen materially, so announcing it is the difference between "the
      // page changed for no reason" and "I know what I'm being asked for".
      setFallbackNote(tryAnotherMethodNote(AADHAAR_METHOD_LABEL[aadhaarScreenKind(next)]));
      setActiveIdx(activeIdx + 1);
      setImageDataUrl(null);
      setQrPayload(null);
    } else {
      toast.error(CHECK_FAILED_TOAST);
      setFallbackNote(RETRY_EXHAUSTED_NOTE);
      setActiveIdx(0);
      setImageDataUrl(null);
      setQrPayload(null);
    }
  }

  /**
   * Submits the step.
   *
   * `overrideInput` exists for the live QR scanner, which submits the instant it reads a
   * code. Its payload cannot go through React state first: `setQrPayload` followed by a
   * submit in the same tick would read the PREVIOUS state, and waiting a render just to
   * submit would be a visible pause on the one path meant to feel instant. So the scanner
   * hands its payload straight in, and the normal button path passes nothing and keeps
   * using buildInput exactly as before.
   */
  function handleSubmit(overrideInput?: Record<string, unknown>) {
    if (!overrideInput && !validate(kind)) return;

    mutate(
      {
        stepId: step.id,
        input: overrideInput ?? buildInput(kind),
        catalogueEntryId: active.catalogueEntryId || undefined,
      },
      {
        onSuccess: (data) => {
          const updated = data.steps.find((s) => s.id === step.id);
          // Provisional shared-link mode: fresh step ids won't match — the entry
          // screen navigates to the real run on success. Nothing to do here.
          if (!updated) return;

          if (kind === 'ovse') {
            // OVSE: init returns the short maadhaar intent URL. We draw the QR
            // from it client-side (no popup, no stored base64). The applicant
            // scans it with the Aadhaar App; the webhook completes it and the
            // poll advances the flow.
            const url = findRequestUrl(updated?.resultJson ?? null);
            if (url) {
              setOvseUrl(url);
              setPhase('awaiting');
              return;
            }
            advanceOrError(updated);
            return;
          }

          if (isAsyncKind(kind)) {
            // DigiLocker: init returns a redirect URL → send the SAME tab there.
            // The gateway returns to digilockerReturnUrl when done.
            const url = findRequestUrl(updated?.resultJson ?? null);
            if (url) {
              setPhase('awaiting');
              goToDigilocker(url);
              return;
            }
            // No URL → the init call itself failed (e.g. missing webhook config).
            advanceOrError(updated);
            return;
          }

          // OCR (synchronous): Passed / Review = done, parent advances.
          if (updated?.status === 'Passed' || updated?.status === 'Review') return;
          advanceOrError(updated);
        },
        onError: (error) => {
          if (isSessionExpiredError(error)) {
            setExpired(true);
            return;
          }
          toast.error('Could not reach the server. Please try again.');
        },
      },
    );
  }

  function onFileSelected(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setImageDataUrl(dataUrl);
      // Secure QR only: try to read the QR's decimal payload here, on the device, so the
      // request can carry `payload` instead of `data`. That skips the server's image
      // decode — the part that struggles with a real photo of a card. Fire-and-forget:
      // the image is already accepted, and a failure just means we send the image.
      setQrPayload(null);
      if (kind === 'secure-qr') {
        void readAadhaarQrPayload(dataUrl).then(setQrPayload);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  // Scanning is only ever a Secure QR affordance. Derived rather than trusted from state
  // alone: a technical fallback can move the run to a different Aadhaar method while
  // `scanning` is still true, and without this the bottom CTA would read "Waiting for the
  // QR code…" on an OCR screen that has no scanner on it.
  const showScanner = scanning && kind === 'secure-qr';

  const isPin = kind === 'digilocker-pin';
  const isImageCapture = kind === 'ocr' || kind === 'secure-qr';
  const ctaLabel = kind === 'ovse' ? 'Start verification' : 'Continue';

  return (
    // Same vertical rhythm as the PAN form and the designer's own preview: their
    // `.sc-workflow-preview__form` works out to ~10px between blocks, where this was
    // 16px. On a phone that difference is the margin between fitting and scrolling.
    //
    // `flex-1` is what lets the CTA below carry `mt-auto` and land at the BOTTOM of
    // the card, level with every other step. Without it this runner sized to its
    // content, so Continue sat directly under the last paragraph with dead space
    // beneath — the button visibly jumped as the applicant moved between steps.
    <div className="flex flex-1 flex-col gap-2.5">
      {/* DEV-ONLY: simulate a verified result to exercise the step's Risk /
          Advanced Rules for any Aadhaar method (incl. async DigiLocker/OVSE)
          without a live provider. Stripped from production builds. */}
      {import.meta.env.DEV && (
        <AadhaarDevSimulator
          sessionId={session.id}
          stepId={step.id}
          publicToken={session.publicToken}
        />
      )}

      {fallbackNote && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
          {fallbackNote}
        </div>
      )}

      {/* DigiLocker (PIN / PIN-less) input screen — the mobile form (input phase). */}
      {isAsyncKind(kind) && phase === 'input' && (
        <>
          <PreviewHeader title="Aadhaar Verification" subtitle="Verify your Aadhaar" />
          <div className="mb-1 w-full">
            <AadhaarCardMock large />
          </div>
          <Field
            label="Mobile Number"
            placeholder="98XXXXXX10"
            // With PIN the applicant supplies everything inside DigiLocker, so this is a
            // convenience only — marked Optional and hinted as such, because a red asterisk
            // over a field that does not block Continue is a straight contradiction.
            optional={isPin}
            hint={
              isPin
                ? 'Optional — you can continue without it'
                : 'Linked to Aadhaar — required to continue'
            }
            value={mobile}
            onChange={setMobile}
            // Digits only, so a phone opens the keypad instead of the alphabet keyboard.
            // The builder PREVIEW of this same screen already passed it
            // (AadhaarDigilockerScreen), so the mock opened a keypad while the real run
            // did not — the one live Field in the whole run that was missing it.
            inputMode="numeric"
          />
          <p className="text-[14px] leading-tight text-[#666666]">
            {isPin
              ? "You'll be securely taken to DigiLocker to enter your Aadhaar number, OTP and security PIN. You'll return here automatically when you finish."
              : "You'll be securely taken to DigiLocker to log in and share your Aadhaar. You'll return here automatically when you finish."}
          </p>
        </>
      )}

      {/* DigiLocker awaiting screen — shown when the applicant is coming back to a
          step that already started (returned from DigiLocker or resumed) and it's
          not finished yet. Offers a same-tab re-entry + waits for the webhook. */}
      {isAsyncKind(kind) && phase === 'awaiting' && (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <ShieldCheck className="h-7 w-7 text-primary" />
          </div>
          <h3 className="text-[16px] font-semibold text-[#111827]">Verify with DigiLocker</h3>
          <p className="max-w-sm text-[14px] leading-relaxed text-[#666666]">
            You'll be securely redirected to DigiLocker to verify your Aadhaar. After you finish,
            you'll be brought back here automatically.
          </p>
          <p className="flex items-center gap-2 text-[13px] text-[#666666]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Waiting for verification…
          </p>
        </div>
      )}

      {/* OVSE (Aadhaar App) screen — real QR appears after Start */}
      {kind === 'ovse' && <AadhaarOvseScreen large qrSrc={ovseQr} />}

      {/* Image capture (upload / scan) — Aadhaar OCR or Secure QR */}
      {isImageCapture && (
        <>
          {kind === 'secure-qr' ? (
            <AadhaarSecureQrScreen>
              {showScanner ? (
                <AadhaarQrScanner
                  // Submits straight from the scan — the applicant presses nothing. The
                  // payload is passed directly rather than through state; see handleSubmit.
                  onDetected={(payload) => {
                    setScanning(false);
                    setQrPayload(payload);
                    handleSubmit({ payload });
                  }}
                  onCancel={() => setScanning(false)}
                />
              ) : imageDataUrl ? (
                <img
                  src={imageDataUrl}
                  alt="Captured Aadhaar Secure QR"
                  className="h-[190px] w-full rounded-xl object-contain"
                />
              ) : undefined}
            </AadhaarSecureQrScreen>
          ) : (
            <AadhaarOcrScreen>
              {imageDataUrl ? (
                <img
                  src={imageDataUrl}
                  alt="Captured Aadhaar"
                  className="h-[190px] w-full rounded-xl object-cover"
                />
              ) : undefined}
            </AadhaarOcrScreen>
          )}
          {/* Camera capture only — Capture / Recapture. No file upload (the applicant
              photographs the Aadhaar card / QR live on their phone).

              Hidden while the live scanner is open: it carries its own "take a photo
              instead", so showing this as well would offer the same escape twice. */}
          {!showScanner && (
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary py-2.5 text-sm font-medium text-primary hover:bg-primary/5"
            >
              <Camera size={16} />
              {imageDataUrl
                ? kind === 'secure-qr'
                  ? 'Recapture QR'
                  : 'Recapture Aadhaar'
                : kind === 'secure-qr'
                  ? 'Capture QR'
                  : 'Capture Aadhaar'}
            </button>
          )}

          {/* The way back INTO scanning — after "take a photo instead", or after a photo
              that could not be read. Only where scanning is actually possible. */}
          {!showScanner && kind === 'secure-qr' && canScanQrLive() && (
            <button
              type="button"
              onClick={() => {
                setImageDataUrl(null);
                setQrPayload(null);
                setScanning(true);
              }}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#E5E7EB] py-2 text-[13px] font-medium text-[#374151] hover:bg-gray-50"
            >
              <ScanLine size={15} />
              Scan the QR with the camera
            </button>
          )}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={onFileSelected}
          />
        </>
      )}

      {/* Bottom CTA:
          • OVSE awaiting → Cancel (QR + hint are above);
          • DigiLocker awaiting → same-tab "Continue to DigiLocker" (re-entry);
          • otherwise → the Start / Verify CTA.
          `mt-auto` on the wrapper rather than on each button: all three branches are
          the same bottom slot, so pinning it once means a new branch can't forget. */}
      <div className="mt-auto">
        {kind === 'ovse' && phase === 'awaiting' ? (
          <button
            type="button"
            onClick={() => {
              setPhase('input');
              setOvseUrl(null);
              setOvseQr(null);
            }}
            className="w-full rounded-xl border border-[#E5E7EB] py-3 text-[15px] font-medium text-[#374151] hover:bg-gray-50"
          >
            Cancel
          </button>
        ) : isAsyncKind(kind) && phase === 'awaiting' ? (
          <button
            type="button"
            onClick={() => {
              const url = requestUrlRef.current;
              if (url) goToDigilocker(url);
            }}
            disabled={!requestUrlRef.current}
            style={{
              backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
              color: 'var(--brand-btn-text, #ffffff)',
              borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
              borderRadius: 'var(--brand-btn-radius, 0.75rem)',
            }}
            className="flex w-full items-center justify-center gap-2 border py-3 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ExternalLink size={15} />
            Continue to DigiLocker
          </button>
        ) : showScanner ? (
          // While the scanner is open there is nothing to press: it submits on its own the
          // moment it reads the code. A Verify button here would be permanently disabled
          // (no image captured yet), which reads as the screen being stuck.
          <p className="m-0 py-3 text-center text-[13px] text-[#6B7280]">
            {isPending ? 'Verifying…' : 'Waiting for the QR code…'}
          </p>
        ) : (
          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={isPending || !validate(kind)}
            style={{
              backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
              color: 'var(--brand-btn-text, #ffffff)',
              borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
              borderRadius: 'var(--brand-btn-radius, 0.75rem)',
            }}
            className="flex w-full items-center justify-center gap-2 border py-3 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isPending && <Loader2 size={15} className="animate-spin" />}
            {ctaLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// Shown when an Aadhaar step is rejected (business Failed). The verification
// returned a definitive negative result, which ends the session (now Rejected).
// This is terminal — no retry.
function FailureScreen({ reason }: { reason?: string | null }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
      <div className="mb-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#DC2626]">
        <X size={60} strokeWidth={3} color="white" />
      </div>
      <h2 className="mb-2 text-[length:var(--text-20)] font-semibold leading-tight text-[#DC2626]">
        Verification failed
      </h2>
      <p className="text-[length:var(--text-14)] text-[#666666]">
        {reason ?? 'The details entered could not be verified. This session has been rejected.'}
      </p>
    </div>
  );
}
